import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Code2,
  Copy,
  ExternalLink,
  FileText,
  FlaskConical,
  Headphones,
  Image as ImageIcon,
  Key,
  PlugZap,
  Quote,
  RefreshCw,
  Send,
  Smile,
  Smartphone,
  Terminal,
  Unplug,
  XCircle,
} from "lucide-react";

import {
  generateInstanceToken,
  listConversations,
  listQRInstances,
  type CurrentUser,
} from "../../lib/api";

type TestLog = {
  id: number;
  time: string;
  kind: "request" | "success" | "error" | "event";
  title: string;
  detail: unknown;
  durationMs?: number;
};

const PUBLIC_API_ROOT = "/api/inbox/v1";

function pretty(value: unknown) {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

type TabType =
  | "standard_text"
  | "quoted_reply"
  | "image"
  | "document"
  | "audio"
  | "reaction"
  | "mark_read"
  | "template"
  | "message_status";

const VERIFIED_OPERATIONS = [
  {
    num: 1,
    id: "standard_text" as TabType,
    type: "Standard Text",
    endpoint: "POST /api/inbox/v1/messages/send-text",
    payload: '{"phone": "+917400109503", "text": "1. Standard Text Message via External API 💬"}',
    serverId: "3EB0E29D9EDD4228",
    status: "Delivered & Read (read)",
    statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    num: 2,
    id: "quoted_reply" as TabType,
    type: "Quoted Reply",
    endpoint: "POST /api/inbox/v1/chats/{chat_id}/messages/text",
    payload: '{"text": "2. Quoted Reply to Message #1 ↩️", "quoted_message_id": "..."}',
    serverId: "3EB0484C2B6402A9",
    status: "Delivered & Read (read)",
    statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    num: 3,
    id: "image" as TabType,
    type: "Image Attachment",
    endpoint: "POST /api/inbox/v1/messages/send-media",
    payload: 'Multipart: phone, media_type="image", caption, attachment',
    serverId: "3EB094CEA6F6B332",
    status: "Delivered & Read (read)",
    statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    num: 4,
    id: "document" as TabType,
    type: "Document / PDF",
    endpoint: "POST /api/inbox/v1/messages/send-media",
    payload: 'Multipart: phone, media_type="document", caption, attachment',
    serverId: "3EB0C9F3071A91AB",
    status: "Delivered & Read (read)",
    statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    num: 5,
    id: "audio" as TabType,
    type: "Audio / Voice",
    endpoint: "POST /api/inbox/v1/messages/send-media",
    payload: 'Multipart: phone, media_type="audio", caption, attachment',
    serverId: "3EB047F04A09AC2B",
    status: "Delivered (sent)",
    statusColor: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    num: 6,
    id: "reaction" as TabType,
    type: "Emoji Reaction",
    endpoint: "PUT /api/inbox/v1/messages/{id}/reaction",
    payload: '{"emoji": "🎉"}',
    serverId: "Reaction Attached",
    status: "Reacted 🎉",
    statusColor: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    num: 7,
    id: "mark_read" as TabType,
    type: "Mark as Read",
    endpoint: "POST /api/inbox/v1/chats/{chat_id}/read",
    payload: "Empty Body",
    serverId: "Receipt Acknowledged",
    status: "unread_count: 0",
    statusColor: "bg-purple-50 text-purple-700 border-purple-200",
  },
  {
    num: 8,
    id: "template" as TabType,
    type: "Template Send",
    endpoint: "POST /api/inbox/v1/templates/{id}/send",
    payload: '{"phone_number":"+917400109503","variables":{"name":"Customer"}}',
    serverId: "Runtime response",
    status: "Available for test",
    statusColor: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    num: 9,
    id: "message_status" as TabType,
    type: "Message Status",
    endpoint: "GET /api/inbox/v1/messages/{id}/status",
    payload: "Message UUID",
    serverId: "Status history",
    status: "Available for test",
    statusColor: "bg-blue-50 text-blue-700 border-blue-200",
  },
];

export function QRApiTestView({
  token,
  user,
  tenantId,
}: {
  token: string;
  user: CurrentUser;
  tenantId?: string;
}) {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const logSequence = useRef(0);

  const [logs, setLogs] = useState<TestLog[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(
    searchParams.get("instance_id") || ""
  );
  const [selectedConversationId, setSelectedConversationId] = useState("");

  // Instance API Token management
  // Secrets intentionally live only in component memory and disappear on refresh.
  const [instanceTokens, setInstanceTokens] = useState<Record<string, string>>({});
  const [customTokenInput, setCustomTokenInput] = useState("");

  const [activeTab, setActiveTab] = useState<TabType>("standard_text");
  const [codeLanguage, setCodeLanguage] = useState<"curl" | "python" | "node" | "php">("curl");

  // Form states for test requests
  const [phoneTarget, setPhoneTarget] = useState("+917400109503");
  const [textMessage, setTextMessage] = useState("1. Standard Text Message via External API 💬");
  const [quotedReplyText, setQuotedReplyText] = useState("2. Quoted Reply to Message #1 ↩️");
  const [quotedMessageId, setQuotedMessageId] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaCaption, setMediaCaption] = useState("Attached Media from WA Master External API");
  const [targetMessageId, setTargetMessageId] = useState("");
  const [targetEmoji, setTargetEmoji] = useState("🎉");
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; language: string }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateVariables, setTemplateVariables] = useState('{"name":"Customer"}');

  const [busy, setBusy] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);

  const effectiveTenantId = tenantId || user.tenant_id;

  const addLog = useCallback(
    (kind: TestLog["kind"], title: string, detail: unknown, durationMs?: number) => {
      const entry: TestLog = {
        id: ++logSequence.current,
        time: new Date().toLocaleTimeString(),
        kind,
        title,
        detail,
        durationMs,
      };
      setLogs((old) => [entry, ...old].slice(0, 100));
    },
    []
  );

  const instancesQuery = useQuery({
    queryKey: ["inbox-api-lab", "instances", effectiveTenantId],
    queryFn: () => listQRInstances(token, effectiveTenantId),
    refetchInterval: 5_000,
  });

  const conversationsQuery = useQuery({
    queryKey: ["inbox-api-lab", "conversations", effectiveTenantId],
    queryFn: () => listConversations(token, undefined, effectiveTenantId),
    refetchInterval: 5_000,
  });

  const instances = useMemo(() => instancesQuery.data || [], [instancesQuery.data]);
  const conversations = useMemo(() => conversationsQuery.data || [], [conversationsQuery.data]);

  // Set default instance if none selected
  useEffect(() => {
    if (!selectedInstanceId && instances.length > 0) {
      const connected = instances.find((i) => i.status === "connected");
      setSelectedInstanceId(connected ? connected.id : (instances[0]?.id || ""));
    }
  }, [instances, selectedInstanceId]);

  const selectedInstance = useMemo(
    () => instances.find((i) => i.id === selectedInstanceId),
    [instances, selectedInstanceId]
  );

  // Set default conversation if available
  useEffect(() => {
    if (!selectedConversationId && conversations.length > 0) {
      setSelectedConversationId(conversations[0]?.id || "");
    }
  }, [conversations, selectedConversationId]);

  // Active token for external API calls
  const activeInstanceToken = useMemo(() => {
    if (selectedInstanceId && instanceTokens[selectedInstanceId]) {
      return instanceTokens[selectedInstanceId];
    }
    if (customTokenInput.trim()) {
      return customTokenInput.trim();
    }
    return "";
  }, [selectedInstanceId, instanceTokens, customTokenInput]);

  const saveInstanceSecret = (instanceId: string, secret: string) => {
    const updated = { ...instanceTokens, [instanceId]: secret.trim() };
    setInstanceTokens(updated);
  };

  useEffect(() => {
    if (!activeInstanceToken) {
      setTemplates([]);
      return;
    }
    let cancelled = false;
    void fetch(`${PUBLIC_API_ROOT}/templates`, {
      headers: { "X-Instance-Token": activeInstanceToken, Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Template request failed (${response.status})`);
        return response.json();
      })
      .then((items) => {
        if (cancelled || !Array.isArray(items)) return;
        setTemplates(items);
        setSelectedTemplateId((current) => current || items[0]?.id || "");
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeInstanceToken]);

  // Real-time WebSocket connection using Instance Token
  useEffect(() => {
    if (!activeInstanceToken) return;
    let closed = false;
    let socket: WebSocket | undefined;

    const connect = async () => {
      try {
        const ticketResponse = await fetch(`${PUBLIC_API_ROOT}/realtime/tickets`, {
          method: "POST",
          headers: { "X-Instance-Token": activeInstanceToken, Accept: "application/json" },
        });
        if (!ticketResponse.ok) throw new Error(`Ticket request failed (${ticketResponse.status})`);
        const ticketBody = await ticketResponse.json();
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsUrl = `${protocol}//${window.location.host}${ticketBody.websocket_url}`;
        socket = new WebSocket(wsUrl);
      socket.onopen = () => {
        if (!closed) {
          setSocketConnected(true);
            addLog("event", "WebSocket Connected", "Connected with a one-time realtime ticket");
        }
      };
      socket.onmessage = (event) => {
        if (closed) return;
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.event !== "ping" && parsed.event !== "pong") {
            addLog("event", `Realtime: ${parsed.event || "event"}`, parsed);
          }
        } catch {
          addLog("event", "Realtime Raw Message", event.data);
        }
      };
      socket.onerror = () => {
        if (!closed) setSocketConnected(false);
      };
      socket.onclose = () => {
        if (!closed) setSocketConnected(false);
      };
      } catch (error) {
        if (!closed) {
          setSocketConnected(false);
          addLog("error", "WebSocket connection failed", error instanceof Error ? error.message : error);
        }
      }
    };
    void connect();

    return () => {
      closed = true;
      socket?.close();
    };
  }, [activeInstanceToken, addLog]);

  // Execute API Request helper
  const executeApi = async (name: string, fn: () => Promise<Response>) => {
    setBusy(true);
    const start = performance.now();
    addLog("request", name, "Sending request with X-Instance-Token header...");
    try {
      const resp = await fn();
      const elapsed = Math.round(performance.now() - start);
      const text = await resp.text();
      let body: any;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        body = text;
      }

      if (resp.ok) {
        addLog("success", `${name} (${resp.status} ${resp.statusText})`, body, elapsed);
        queryClient.invalidateQueries({ queryKey: ["inbox-api-lab"] });

        // If returned message has an ID, auto-fill for quick reaction / quote testing
        if (body && body.id) {
          setTargetMessageId(body.id);
          setQuotedMessageId(body.id);
          if (body.conversation_id) {
            setSelectedConversationId(body.conversation_id);
          }
        }
      } else {
        addLog("error", `${name} (${resp.status} ${resp.statusText})`, body, elapsed);
      }
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      addLog("error", `${name} Failed`, err instanceof Error ? err.message : err, elapsed);
    } finally {
      setBusy(false);
    }
  };

  const getHeaders = (isMultipart = false) => {
    const h: Record<string, string> = { Accept: "application/json" };
    if (!isMultipart) h["Content-Type"] = "application/json";

    if (activeInstanceToken) h["X-Instance-Token"] = activeInstanceToken;
    return h;
  };

  // Helper to create synthetic dummy files for 1-click media testing if none uploaded
  const ensureSampleFile = (type: "image" | "document" | "audio"): File => {
    if (mediaFile) return mediaFile;
    if (type === "image") {
      // 1x1 green PNG
      const pngBase64 =
        "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";
      const byteCharacters = atob(pngBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new File([byteArray], "sample_image.png", { type: "image/png" });
    }
    if (type === "document") {
      const content = "%PDF-1.4\n%WA Master Sample PDF Document\n1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj\n2 0 obj<</Type/Pages/Count 1/Kids[3 0 R]>>endobj\n3 0 obj<</Type/Page/MediaBox[0 0 300 300]/Parent 2 0 R>>endobj\nxref\n0 4\n0000000000 65535 f\n0000000010 00000 n\n0000000060 00000 n\n0000000115 00000 n\ntrailer<</Size 4/Root 1 0 R>>\nstartxref\n185\n%%EOF";
      return new File([content], "invoice_sample.pdf", { type: "application/pdf" });
    }
    // audio/wav tiny beep
    const wavBytes = new Uint8Array([
      0x52, 0x49, 0x46, 0x46, 0x24, 0x00, 0x00, 0x00, 0x57, 0x41, 0x56, 0x45, 0x66, 0x6d, 0x74, 0x20,
      0x10, 0x00, 0x00, 0x00, 0x01, 0x00, 0x01, 0x00, 0x44, 0xac, 0x00, 0x00, 0x88, 0x58, 0x01, 0x00,
      0x02, 0x00, 0x10, 0x00, 0x64, 0x61, 0x74, 0x61, 0x00, 0x00, 0x00, 0x00,
    ]);
    return new File([wavBytes], "voice_note.wav", { type: "audio/wav" });
  };

  // 1. Direct Send Text
  const handleSendText = () => {
    executeApi("POST /inbox/messages/send-text", () =>
      fetch(`${PUBLIC_API_ROOT}/messages/send-text`, {
        method: "POST",
        headers: { ...getHeaders(), "Idempotency-Key": `api-lab-text-${Date.now()}` },
        body: JSON.stringify({
          phone: phoneTarget.trim(),
          text: textMessage,
        }),
      })
    );
  };

  // 2. Quoted Reply
  const handleSendQuoted = () => {
    if (!selectedConversationId) {
      alert("Please select or enter a Conversation ID first");
      return;
    }
    executeApi(`POST /inbox/chats/${selectedConversationId}/messages/text`, () =>
      fetch(`${PUBLIC_API_ROOT}/chats/${selectedConversationId}/messages/text`, {
        method: "POST",
        headers: { ...getHeaders(), "Idempotency-Key": `api-lab-reply-${Date.now()}` },
        body: JSON.stringify({
          text: quotedReplyText,
          quoted_message_id: quotedMessageId.trim() || undefined,
        }),
      })
    );
  };

  // 3. Image Attachment
  const handleSendImage = () => {
    const file = ensureSampleFile("image");
    const formData = new FormData();
    formData.append("phone", phoneTarget.trim());
    formData.append("media_type", "image");
    formData.append("caption", mediaCaption || "Sample Image Attachment 📷");
    formData.append("attachment", file);

    executeApi("POST /inbox/messages/send-media (image)", () =>
      fetch(`${PUBLIC_API_ROOT}/messages/send-media`, {
        method: "POST",
        headers: { ...getHeaders(true), "Idempotency-Key": `api-lab-image-${Date.now()}` },
        body: formData,
      })
    );
  };

  // 4. Document / PDF
  const handleSendDocument = () => {
    const file = ensureSampleFile("document");
    const formData = new FormData();
    formData.append("phone", phoneTarget.trim());
    formData.append("media_type", "document");
    formData.append("caption", mediaCaption || "Sample PDF Document 📄");
    formData.append("attachment", file);

    executeApi("POST /inbox/messages/send-media (document)", () =>
      fetch(`${PUBLIC_API_ROOT}/messages/send-media`, {
        method: "POST",
        headers: { ...getHeaders(true), "Idempotency-Key": `api-lab-document-${Date.now()}` },
        body: formData,
      })
    );
  };

  // 5. Audio / Voice
  const handleSendAudio = () => {
    const file = ensureSampleFile("audio");
    const formData = new FormData();
    formData.append("phone", phoneTarget.trim());
    formData.append("media_type", "audio");
    formData.append("caption", mediaCaption || "Sample Audio / Voice Note 🎵");
    formData.append("attachment", file);

    executeApi("POST /inbox/messages/send-media (audio)", () =>
      fetch(`${PUBLIC_API_ROOT}/messages/send-media`, {
        method: "POST",
        headers: { ...getHeaders(true), "Idempotency-Key": `api-lab-audio-${Date.now()}` },
        body: formData,
      })
    );
  };

  // 6. Emoji Reaction
  const handleSendReaction = () => {
    if (!targetMessageId.trim()) {
      alert("Enter a target message UUID to react to");
      return;
    }
    executeApi(`PUT /messages/${targetMessageId.trim()}/reaction`, () =>
      fetch(`${PUBLIC_API_ROOT}/messages/${targetMessageId.trim()}/reaction`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ emoji: targetEmoji }),
      })
    );
  };

  // 7. Mark Chat Read
  const handleMarkRead = () => {
    if (!selectedConversationId) {
      alert("Select a chat conversation first");
      return;
    }
    executeApi(`POST /inbox/chats/${selectedConversationId}/read`, () =>
      fetch(`${PUBLIC_API_ROOT}/chats/${selectedConversationId}/read`, {
        method: "POST",
        headers: getHeaders(),
      })
    );
  };

  const handleSendTemplate = () => {
    if (!selectedTemplateId) {
      alert("Select a template first");
      return;
    }
    let variables: Record<string, string>;
    try {
      variables = JSON.parse(templateVariables);
    } catch {
      alert("Template variables must be valid JSON");
      return;
    }
    executeApi(`POST /templates/${selectedTemplateId}/send`, () =>
      fetch(`${PUBLIC_API_ROOT}/templates/${selectedTemplateId}/send`, {
        method: "POST",
        headers: { ...getHeaders(), "Idempotency-Key": `api-lab-template-${Date.now()}` },
        body: JSON.stringify({
          phone_number: phoneTarget.trim(),
          variables,
        }),
      })
    );
  };

  const handleCheckMessageStatus = () => {
    if (!targetMessageId.trim()) {
      alert("Enter a message UUID first");
      return;
    }
    executeApi(`GET /messages/${targetMessageId.trim()}/status`, () =>
      fetch(`${PUBLIC_API_ROOT}/messages/${targetMessageId.trim()}/status`, {
        headers: getHeaders(),
      })
    );
  };

  // Dynamic values for Environment variables
  const apiBaseUrl = `${window.location.origin}${PUBLIC_API_ROOT}`;
  const websocketUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${
    window.location.host
  }${PUBLIC_API_ROOT}/realtime`;
  const instancePhone = selectedInstance?.phone_number || "919653465441";
  const currentInstanceId = selectedInstanceId || "b7632008-d3a0-47c1-a44c-f175f299bdee";
  const currentTenantId = effectiveTenantId || "0549d05c-5098-4d5a-b44e-fc0fa6e50907";
  const currentConvId = selectedConversationId || "f498c696-5084-4647-b4c8-265e295cc2a1";
  const currentMsgId = targetMessageId || quotedMessageId || "WA Master message UUID";

  // Multi-language code snippets
  const codeSnippet = useMemo(() => {
    const tokenHeader = `X-Instance-Token: ${activeInstanceToken || "wam_inst_live_xxxxxxxxxxxxxxxxxxxxxxxx"}`;
    const tokenValue = activeInstanceToken || "wam_inst_live_xxxxxxxxxxxxxxxxxxxxxxxx";

    // 1. Standard Text
    if (activeTab === "standard_text") {
      const url = `${apiBaseUrl}/messages/send-text`;
      const payload = {
        phone: phoneTarget,
        text: textMessage,
      };

      if (codeLanguage === "curl") {
        return `curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "${tokenHeader}" \\
  -d '${JSON.stringify(payload, null, 2)}'`;
      }
      if (codeLanguage === "python") {
        return `import requests

url = "${url}"
headers = {
    "Content-Type": "application/json",
    "X-Instance-Token": "${tokenValue}"
}
data = ${JSON.stringify(payload, null, 4)}

response = requests.post(url, json=data, headers=headers)
print(response.status_code, response.json())`;
      }
      if (codeLanguage === "node") {
        return `const response = await fetch("${url}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-Instance-Token": "${tokenValue}"
  },
  body: JSON.stringify(${JSON.stringify(payload, null, 4)})
});

const data = await response.json();
console.log(data);`;
      }
      if (codeLanguage === "php") {
        return `<?php
$ch = curl_init("${url}");
$payload = json_encode([
    "phone" => "${phoneTarget}",
    "text" => "${textMessage.replace(/"/g, '\\"')}"
]);

curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json',
    '${tokenHeader}'
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);
echo $response;`;
      }
    }

    // 2. Quoted Reply
    if (activeTab === "quoted_reply") {
      const url = `${apiBaseUrl}/chats/${currentConvId}/messages/text`;
      const payload = {
        text: quotedReplyText,
        quoted_message_id: quotedMessageId || undefined,
      };

      if (codeLanguage === "curl") {
        return `curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "${tokenHeader}" \\
  -d '${JSON.stringify(payload, null, 2)}'`;
      }
      if (codeLanguage === "python") {
        return `import requests

url = "${url}"
headers = {
    "Content-Type": "application/json",
    "X-Instance-Token": "${tokenValue}"
}
data = ${JSON.stringify(payload, null, 4)}

response = requests.post(url, json=data, headers=headers)
print(response.status_code, response.json())`;
      }
      if (codeLanguage === "node") {
        return `const response = await fetch("${url}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-Instance-Token": "${tokenValue}"
  },
  body: JSON.stringify(${JSON.stringify(payload, null, 4)})
});
console.log(await response.json());`;
      }
      if (codeLanguage === "php") {
        return `<?php
$ch = curl_init("${url}");
$payload = json_encode([
    "text" => "${quotedReplyText.replace(/"/g, '\\"')}",
    "quoted_message_id" => "${quotedMessageId || ""}"
]);

curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json',
    '${tokenHeader}'
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);
echo $response;`;
      }
    }

    // 3. Image / 4. Document / 5. Audio
    if (activeTab === "image" || activeTab === "document" || activeTab === "audio") {
      const url = `${apiBaseUrl}/messages/send-media`;
      const mediaType = activeTab;
      const sampleFile =
        mediaType === "image"
          ? "photo.jpg"
          : mediaType === "document"
          ? "document.pdf"
          : "audio.mp3";

      if (codeLanguage === "curl") {
        return `curl -X POST "${url}" \\
  -H "${tokenHeader}" \\
  -F "phone=${phoneTarget}" \\
  -F "media_type=${mediaType}" \\
  -F "caption=${mediaCaption}" \\
  -F "attachment=@/path/to/${sampleFile}"`;
      }
      if (codeLanguage === "python") {
        return `import requests

url = "${url}"
headers = {"X-Instance-Token": "${tokenValue}"}
data = {
    "phone": "${phoneTarget}",
    "media_type": "${mediaType}",
    "caption": "${mediaCaption}"
}
files = {"attachment": open("${sampleFile}", "rb")}

response = requests.post(url, headers=headers, data=data, files=files)
print(response.status_code, response.json())`;
      }
      if (codeLanguage === "node") {
        return `import FormData from 'form-data';
import fs from 'fs';
import fetch from 'node-fetch';

const form = new FormData();
form.append('phone', '${phoneTarget}');
form.append('media_type', '${mediaType}');
form.append('caption', '${mediaCaption}');
form.append('attachment', fs.createReadStream('${sampleFile}'));

const response = await fetch('${url}', {
  method: 'POST',
  headers: {
    'X-Instance-Token': '${tokenValue}',
    ...form.getHeaders()
  },
  body: form
});
console.log(await response.json());`;
      }
      if (codeLanguage === "php") {
        return `<?php
$ch = curl_init("${url}");
$cfile = new CURLFile('${sampleFile}', '${
          mediaType === "image"
            ? "image/jpeg"
            : mediaType === "document"
            ? "application/pdf"
            : "audio/mpeg"
        }', '${sampleFile}');

$data = [
    'phone' => '${phoneTarget}',
    'media_type' => '${mediaType}',
    'caption' => '${mediaCaption}',
    'attachment' => $cfile
];

curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, $data);
curl_setopt($ch, CURLOPT_HTTPHEADER, ['${tokenHeader}']);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$result = curl_exec($ch);
curl_close($ch);
echo $result;`;
      }
    }

    // 6. Emoji Reaction
    if (activeTab === "reaction") {
      const url = `${apiBaseUrl}/messages/${currentMsgId}/reaction`;
      const payload = { emoji: targetEmoji };

      if (codeLanguage === "curl") {
        return `curl -X PUT "${url}" \\
  -H "Content-Type: application/json" \\
  -H "${tokenHeader}" \\
  -d '${JSON.stringify(payload)}'`;
      }
      if (codeLanguage === "python") {
        return `import requests

url = "${url}"
headers = {
    "Content-Type": "application/json",
    "X-Instance-Token": "${tokenValue}"
}

response = requests.put(url, json=${JSON.stringify(payload)}, headers=headers)
print(response.status_code, response.json())`;
      }
      if (codeLanguage === "node") {
        return `const response = await fetch("${url}", {
  method: "PUT",
  headers: {
    "Content-Type": "application/json",
    "X-Instance-Token": "${tokenValue}"
  },
  body: JSON.stringify(${JSON.stringify(payload)})
});
console.log(await response.json());`;
      }
      if (codeLanguage === "php") {
        return `<?php
$ch = curl_init("${url}");
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, 'PUT');
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(['emoji' => '${targetEmoji}']));
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json',
    '${tokenHeader}'
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$result = curl_exec($ch);
curl_close($ch);
echo $result;`;
      }
    }

    // 7. Mark Read
    if (activeTab === "mark_read") {
      const url = `${apiBaseUrl}/chats/${currentConvId}/read`;

      if (codeLanguage === "curl") {
        return `curl -X POST "${url}" \\
  -H "${tokenHeader}"`;
      }
      if (codeLanguage === "python") {
        return `import requests

url = "${url}"
headers = {"X-Instance-Token": "${tokenValue}"}
response = requests.post(url, headers=headers)
print(response.status_code, response.json())`;
      }
      if (codeLanguage === "node") {
        return `const response = await fetch("${url}", {
  method: "POST",
  headers: { "X-Instance-Token": "${tokenValue}" }
});
console.log(await response.json());`;
      }
      if (codeLanguage === "php") {
        return `<?php
$ch = curl_init("${url}");
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_HTTPHEADER, ['${tokenHeader}']);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$result = curl_exec($ch);
curl_close($ch);
echo $result;`;
      }
    }

    if (activeTab === "template") {
      const url = `${apiBaseUrl}/templates/${selectedTemplateId || "TEMPLATE_UUID"}/send`;
      const payload = {
        phone_number: phoneTarget,
        variables: (() => {
          try {
            return JSON.parse(templateVariables);
          } catch {
            return { name: "Customer" };
          }
        })(),
      };
      return `curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "${tokenHeader}" \\
  -H "Idempotency-Key: your-unique-send-key" \\
  -d '${JSON.stringify(payload, null, 2)}'`;
    }

    if (activeTab === "message_status") {
      return `curl -X GET "${apiBaseUrl}/messages/${currentMsgId}/status" \\
  -H "${tokenHeader}"`;
    }

    return `curl -X GET "${apiBaseUrl}/chats" -H "${tokenHeader}"`;
  }, [
    activeTab,
    codeLanguage,
    activeInstanceToken,
    apiBaseUrl,
    phoneTarget,
    textMessage,
    quotedReplyText,
    quotedMessageId,
    mediaCaption,
    currentConvId,
    currentMsgId,
    targetEmoji,
    selectedTemplateId,
    templateVariables,
  ]);

  const copyToClipboard = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="mx-auto max-w-[1600px] space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#008069] mb-1">
            <FlaskConical className="w-4 h-4" />
            <span>Developer Sandbox & Fleet Hub</span>
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">QR API Lab</h1>
          <p className="text-xs text-slate-500 mt-1">
            Official REST & WebSocket endpoints verified on physical WhatsApp multi-device companions.
            Use your persistent <span className="font-mono font-bold text-slate-700">X-Instance-Token</span> for all external integrations.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${
              socketConnected
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-100 text-slate-600 border-slate-200"
            }`}
          >
            {socketConnected ? <PlugZap className="w-3.5 h-3.5" /> : <Unplug className="w-3.5 h-3.5" />}
            <span>WebSocket {socketConnected ? "Connected" : "Idle"}</span>
          </div>

          <Link
            to="/admin/qr-instances"
            className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
          >
            <Smartphone className="w-3.5 h-3.5 text-slate-500" />
            <span>Manage Instances</span>
          </Link>
        </div>
      </div>

      {/* Exact Environment Variables Context Card */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-[#008069]" />
              <h2 className="text-sm font-bold text-slate-900">REST & WebSocket Integration Environment</h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Use these values in every REST integration. External APIs only require the <span className="font-mono font-bold text-slate-700">X-Instance-Token</span> header (no passwords or JWTs needed).
            </p>
          </div>

          <a
            href="/admin/qr-instances"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#008069] bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-xl border border-emerald-200 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>Generate QR manually</span>
          </a>
        </div>

        {/* Environment Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* API_BASE_URL */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                API_BASE_URL
              </span>
              <button
                onClick={() => copyToClipboard(apiBaseUrl, "api_base")}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy API Base URL"
              >
                {copiedKey === "api_base" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-slate-800 font-semibold truncate select-all" title={apiBaseUrl}>
              {apiBaseUrl}
            </div>
          </div>

          {/* X_INSTANCE_TOKEN */}
          <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-800">
                X_INSTANCE_TOKEN
              </span>
              <button
                onClick={() => copyToClipboard(activeInstanceToken, "token_header")}
                className="text-emerald-700 hover:text-emerald-900 p-1"
                title="Copy Instance Token"
              >
                {copiedKey === "token_header" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-emerald-950 font-bold truncate select-all" title={activeInstanceToken}>
              {activeInstanceToken || "wam_inst_live_..."}
            </div>
          </div>

          {/* TENANT_ID */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                TENANT_ID
              </span>
              <button
                onClick={() => copyToClipboard(currentTenantId, "tenant_id")}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy Tenant ID"
              >
                {copiedKey === "tenant_id" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-slate-800 font-semibold truncate select-all" title={currentTenantId}>
              {currentTenantId}
            </div>
          </div>

          {/* INSTANCE_ID */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                INSTANCE_ID
              </span>
              <button
                onClick={() => copyToClipboard(currentInstanceId, "instance_id")}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy Instance ID"
              >
                {copiedKey === "instance_id" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-slate-800 font-semibold truncate select-all" title={currentInstanceId}>
              {currentInstanceId}
            </div>
          </div>

          {/* PHONE_E164 */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                PHONE_E164
              </span>
              <button
                onClick={() => copyToClipboard(instancePhone, "phone_e164")}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy Phone E.164"
              >
                {copiedKey === "phone_e164" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-slate-800 font-semibold truncate select-all" title={instancePhone}>
              {instancePhone}
            </div>
          </div>

          {/* CONVERSATION_ID */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                CONVERSATION_ID
              </span>
              <button
                onClick={() => copyToClipboard(currentConvId, "conv_id")}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy Conversation ID"
              >
                {copiedKey === "conv_id" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-slate-800 font-semibold truncate select-all" title={currentConvId}>
              {currentConvId}
            </div>
          </div>

          {/* MESSAGE_ID */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                MESSAGE_ID
              </span>
              <button
                onClick={() => copyToClipboard(currentMsgId, "msg_id")}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy Message ID"
              >
                {copiedKey === "msg_id" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-slate-800 font-semibold truncate select-all" title={currentMsgId}>
              {currentMsgId}
            </div>
          </div>

          {/* WEBSOCKET_URL */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                WEBSOCKET_URL
              </span>
              <button
                onClick={() => copyToClipboard(websocketUrl, "ws_url")}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Copy WebSocket URL"
              >
                {copiedKey === "ws_url" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="font-mono text-xs text-slate-800 font-semibold truncate select-all" title={websocketUrl}>
              {websocketUrl}
            </div>
          </div>
        </div>

        {/* Active Instance Token Context Bar */}
        <div className="pt-2 border-t border-slate-100 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="w-full lg:w-72">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Active WhatsApp Instance
              </label>
              <select
                value={selectedInstanceId}
                onChange={(e) => setSelectedInstanceId(e.target.value)}
                className="w-full text-xs font-semibold px-3 py-1.5 rounded-xl border border-slate-300 bg-white focus:outline-none focus:border-[#008069]"
              >
                {instances.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name} ({inst.phone_number || "No Phone"}) — {inst.status}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 lg:w-80">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                X-Instance-Token Secret
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  placeholder={
                    selectedInstanceId && instanceTokens[selectedInstanceId]
                      ? "•••••••••••••••••••••••• (Saved)"
                      : "Enter raw wam_inst_live_..."
                  }
                  value={customTokenInput}
                  onChange={(e) => setCustomTokenInput(e.target.value)}
                  onBlur={() => {
                    if (customTokenInput.trim() && selectedInstanceId) {
                      saveInstanceSecret(selectedInstanceId, customTokenInput.trim());
                    }
                  }}
                  className="w-full text-xs font-mono px-3 py-1.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                />
                {selectedInstanceId && instanceTokens[selectedInstanceId] && (
                  <button
                    onClick={() =>
                      copyToClipboard(instanceTokens[selectedInstanceId] || "", "active_secret")
                    }
                    className="btn-secondary text-xs py-1.5 px-2.5 whitespace-nowrap"
                    title="Copy Saved Instance Token"
                  >
                    {copiedKey === "active_secret" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full lg:w-auto justify-end">
            <button
              onClick={async () => {
                if (!selectedInstanceId) return;
                try {
                  const res = await generateInstanceToken(token, selectedInstanceId);
                  saveInstanceSecret(selectedInstanceId, res.token);
                  queryClient.invalidateQueries({ queryKey: ["inbox-api-lab"] });
                  alert(`New token generated and saved:\n${res.token}`);
                } catch (e: any) {
                  alert(e?.message || "Failed to generate token");
                }
              }}
              className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Regenerate Instance Token</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid: Tester on Left, Snippets & Logs on Right */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Left Column: Interactive 7 Operations Tester (7 cols) */}
        <div className="xl:col-span-7 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            {/* 7 Operation Tabs */}
            <div className="flex border-b border-slate-200 overflow-x-auto bg-slate-50/70 p-1.5 gap-1">
              {[
                { id: "standard_text", label: "1. Text", icon: Send },
                { id: "quoted_reply", label: "2. Quoted Reply", icon: Quote },
                { id: "image", label: "3. Image", icon: ImageIcon },
                { id: "document", label: "4. Document", icon: FileText },
                { id: "audio", label: "5. Audio", icon: Headphones },
                { id: "reaction", label: "6. Reaction", icon: Smile },
                { id: "mark_read", label: "7. Mark Read", icon: CheckCircle2 },
                { id: "template", label: "8. Template", icon: Code2 },
                { id: "message_status", label: "9. Status", icon: RefreshCw },
              ].map((tab) => {
                const Icon = tab.icon;
                const isCurrent = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                      isCurrent
                        ? "bg-white text-[#008069] shadow-2xs"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Common Phone Input for Sending */}
              {["standard_text", "image", "document", "audio", "template"].includes(activeTab) && (
                <div className="mb-4 pb-4 border-b border-slate-100">
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Recipient Phone Number (E.164 format)
                  </label>
                  <input
                    type="text"
                    value={phoneTarget}
                    onChange={(e) => setPhoneTarget(e.target.value)}
                    placeholder="+917400109503"
                    className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                  />
                </div>
              )}

              {/* Tab 1: Standard Text */}
              {activeTab === "standard_text" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
        POST /api/inbox/v1/messages/send-text
                      </h3>
                      <p className="text-xs text-slate-500">
                        Sends a plain text message using the bound WhatsApp instance.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      POST
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Message Content (text)
                      </label>
                      <textarea
                        rows={3}
                        value={textMessage}
                        onChange={(e) => setTextMessage(e.target.value)}
                        placeholder="Type message content..."
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>

                    <button
                      onClick={handleSendText}
                      disabled={busy}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <Send className="w-3.5 h-3.5" />
                      <span>{busy ? "Sending Message..." : "1. Send Standard Text Message"}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tab 2: Quoted Reply */}
              {activeTab === "quoted_reply" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        POST /api/inbox/v1/chats/&#123;chat_id&#125;/messages/text
                      </h3>
                      <p className="text-xs text-slate-500">
                        Sends a message quoting a previous message in a conversation thread.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      POST
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Target Conversation (chat_id)
                      </label>
                      <select
                        value={selectedConversationId}
                        onChange={(e) => setSelectedConversationId(e.target.value)}
                        className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-slate-300 bg-white focus:outline-none focus:border-[#008069]"
                      >
                        <option value="">-- Choose Conversation --</option>
                        {conversations.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.contact?.phone_e164 || c.id} - {c.last_message_preview?.slice(0, 35)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Quoted Message UUID (quoted_message_id)
                      </label>
                      <input
                        type="text"
                        value={quotedMessageId}
                        onChange={(e) => setQuotedMessageId(e.target.value)}
                        placeholder="UUID of previous message to reply to"
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Reply Content (text)
                      </label>
                      <textarea
                        rows={2}
                        value={quotedReplyText}
                        onChange={(e) => setQuotedReplyText(e.target.value)}
                        placeholder="Reply text..."
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>

                    <button
                      onClick={handleSendQuoted}
                      disabled={busy || !selectedConversationId}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <Quote className="w-3.5 h-3.5" />
                      <span>{busy ? "Sending Quoted..." : "2. Send Quoted Reply"}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tab 3: Image Attachment */}
              {activeTab === "image" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        POST /api/inbox/v1/messages/send-media (image)
                      </h3>
                      <p className="text-xs text-slate-500">
                        Multipart upload with WhatsApp thumbnail & progressive encryption.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Multipart POST
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Upload Image File (or click Send to use sample PNG)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                        className="w-full text-xs p-2 rounded-xl border border-slate-300 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#008069]/10 file:text-[#008069] hover:file:bg-[#008069]/20"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Image Caption (optional)
                      </label>
                      <input
                        type="text"
                        value={mediaCaption}
                        onChange={(e) => setMediaCaption(e.target.value)}
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>

                    <button
                      onClick={handleSendImage}
                      disabled={busy}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <ImageIcon className="w-3.5 h-3.5" />
                      <span>{busy ? "Uploading & Sending..." : "3. Send Image Attachment"}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tab 4: Document / PDF */}
              {activeTab === "document" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        POST /api/inbox/v1/messages/send-media (document)
                      </h3>
                      <p className="text-xs text-slate-500">
                        Transmit documents, spreadsheets, or PDFs with WhatsApp file metadata.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Multipart POST
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Upload Document / PDF (or click Send to use sample PDF)
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.zip"
                        onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                        className="w-full text-xs p-2 rounded-xl border border-slate-300 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#008069]/10 file:text-[#008069] hover:file:bg-[#008069]/20"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        File Title / Caption
                      </label>
                      <input
                        type="text"
                        value={mediaCaption}
                        onChange={(e) => setMediaCaption(e.target.value)}
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>

                    <button
                      onClick={handleSendDocument}
                      disabled={busy}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span>{busy ? "Uploading Document..." : "4. Send Document / PDF"}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tab 5: Audio / Voice */}
              {activeTab === "audio" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        POST /api/inbox/v1/messages/send-media (audio)
                      </h3>
                      <p className="text-xs text-slate-500">
                        Sends an audio file or voice note encoded for WhatsApp playback.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Multipart POST
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Upload Audio File (or click Send to use sample WAV)
                      </label>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                        className="w-full text-xs p-2 rounded-xl border border-slate-300 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#008069]/10 file:text-[#008069] hover:file:bg-[#008069]/20"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Caption (optional)
                      </label>
                      <input
                        type="text"
                        value={mediaCaption}
                        onChange={(e) => setMediaCaption(e.target.value)}
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>

                    <button
                      onClick={handleSendAudio}
                      disabled={busy}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <Headphones className="w-3.5 h-3.5" />
                      <span>{busy ? "Uploading Audio..." : "5. Send Audio / Voice Note"}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tab 6: Emoji Reaction */}
              {activeTab === "reaction" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        PUT /api/inbox/v1/messages/&#123;message_id&#125;/reaction
                      </h3>
                      <p className="text-xs text-slate-500">
                        Attaches or clears an emoji reaction on any sent or received message.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      POST
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Target Message UUID
                      </label>
                      <input
                        type="text"
                        value={targetMessageId}
                        onChange={(e) => setTargetMessageId(e.target.value)}
                        placeholder="UUID of message to react to"
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Reaction Emoji
                      </label>
                      <div className="flex items-center gap-2">
                        {["🎉", "👍", "❤️", "😂", "😮", "🙏", "🔥"].map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => setTargetEmoji(emoji)}
                            className={`w-9 h-9 text-lg rounded-xl border flex items-center justify-center transition-all ${
                              targetEmoji === emoji
                                ? "border-[#008069] bg-emerald-50 shadow-2xs scale-105"
                                : "border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            {emoji}
                          </button>
                        ))}
                        <input
                          type="text"
                          value={targetEmoji}
                          onChange={(e) => setTargetEmoji(e.target.value)}
                          className="w-16 text-center text-sm px-2 py-1.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleSendReaction}
                      disabled={busy || !targetMessageId.trim()}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <Smile className="w-3.5 h-3.5" />
                      <span>{busy ? "Applying Reaction..." : "6. Send Emoji Reaction"}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Tab 7: Mark Read */}
              {activeTab === "mark_read" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        POST /api/inbox/v1/chats/&#123;chat_id&#125;/read
                      </h3>
                      <p className="text-xs text-slate-500">
                        Resets the WA Master unread count to 0 for this instance-scoped chat.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      POST
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Select Conversation
                      </label>
                      <select
                        value={selectedConversationId}
                        onChange={(e) => setSelectedConversationId(e.target.value)}
                        className="w-full text-xs font-semibold px-3 py-2 rounded-xl border border-slate-300 bg-white focus:outline-none focus:border-[#008069]"
                      >
                        <option value="">-- Choose Conversation --</option>
                        {conversations.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.contact?.phone_e164 || c.id} ({c.unread_count} unread)
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={handleMarkRead}
                      disabled={busy || !selectedConversationId}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{busy ? "Marking Read..." : "7. Mark Conversation Read"}</span>
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "template" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        POST /api/inbox/v1/templates/&#123;template_id&#125;/send
                      </h3>
                      <p className="text-xs text-slate-500">
                        Renders a tenant template and sends it through the selected instance.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      POST
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Template</label>
                      <select
                        value={selectedTemplateId}
                        onChange={(event) => setSelectedTemplateId(event.target.value)}
                        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-300 bg-white focus:outline-none focus:border-[#008069]"
                      >
                        <option value="">-- Choose Template --</option>
                        {templates.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} ({item.language})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Variables (JSON object)
                      </label>
                      <textarea
                        rows={3}
                        value={templateVariables}
                        onChange={(event) => setTemplateVariables(event.target.value)}
                        className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                      />
                    </div>
                    <button
                      onClick={handleSendTemplate}
                      disabled={busy || !selectedTemplateId || !activeInstanceToken}
                      className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                    >
                      <Code2 className="w-3.5 h-3.5" />
                      <span>{busy ? "Rendering & Sending..." : "8. Send Template"}</span>
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "message_status" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        GET /api/inbox/v1/messages/&#123;message_id&#125;/status
                      </h3>
                      <p className="text-xs text-slate-500">
                        Returns current delivery state and the complete transition history.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                      GET
                    </span>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Message UUID
                    </label>
                    <input
                      type="text"
                      value={targetMessageId}
                      onChange={(event) => setTargetMessageId(event.target.value)}
                      placeholder="UUID returned by a send or message-list request"
                      className="w-full text-xs font-mono px-3 py-2 rounded-xl border border-slate-300 focus:outline-none focus:border-[#008069]"
                    />
                  </div>
                  <button
                    onClick={handleCheckMessageStatus}
                    disabled={busy || !targetMessageId.trim() || !activeInstanceToken}
                    className="btn-primary w-full py-2.5 text-xs flex items-center justify-center gap-2 shadow-xs"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
                    <span>{busy ? "Checking..." : "9. Check Message Status"}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Multi-language Code Snippets Generator */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-[#008069]" />
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Copy Integration Code ({codeLanguage.toUpperCase()})
                </h3>
              </div>
              <div className="flex items-center gap-1.5">
                {(["curl", "python", "node", "php"] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setCodeLanguage(lang)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition-all ${
                      codeLanguage === lang
                        ? "bg-[#008069] text-white shadow-2xs"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 bg-slate-950 relative group">
              <button
                onClick={() => copyToClipboard(codeSnippet, "snippet")}
                className="absolute top-3 right-3 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-xs"
              >
                {copiedKey === "snippet" ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                <span>{copiedKey === "snippet" ? "Copied" : "Copy"}</span>
              </button>
              <pre className="font-mono text-xs text-emerald-400 overflow-x-auto whitespace-pre p-2">
                {codeSnippet}
              </pre>
            </div>
          </div>
        </div>

        {/* Right Column: Realtime Logs and Traces (5 cols) */}
        <div className="xl:col-span-5 space-y-4">
          {/* Realtime WebSocket Inspector */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden flex flex-col h-[280px]">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div className="flex items-center gap-2">
                <div
                  className={`w-2.5 h-2.5 rounded-full ${
                    socketConnected ? "bg-emerald-500 animate-pulse" : "bg-slate-300"
                  }`}
                />
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Realtime Event Inspector
                </h3>
              </div>
              <button
                onClick={() => setLogs([])}
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
              >
                Clear
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2 font-mono text-xs bg-slate-950 text-slate-200">
              {logs.filter((l) => l.kind === "event").length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-500 text-xs text-center p-4">
                  <PlugZap className="w-6 h-6 mb-2 text-slate-600" />
                  <span>Listening for realtime events...</span>
                  <span className="text-[10px] text-slate-600 mt-1">
                    Inbound messages, delivery ticks, and reactions will stream live.
                  </span>
                </div>
              ) : (
                logs
                  .filter((l) => l.kind === "event")
                  .map((log) => (
                    <div
                      key={log.id}
                      className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 space-y-1"
                    >
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-emerald-400 font-bold">{log.title}</span>
                        <span className="text-slate-500">{log.time}</span>
                      </div>
                      <pre className="text-[10px] text-slate-300 overflow-x-auto whitespace-pre-wrap">
                        {pretty(log.detail)}
                      </pre>
                    </div>
                  ))
              )}
            </div>
          </div>

          {/* Request & Response Trace */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden flex flex-col h-[380px]">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-slate-600" />
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Request & Response Trace
                </h3>
              </div>
              <span className="text-[11px] text-slate-500">
                {logs.filter((l) => l.kind !== "event").length} trace(s)
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2 text-xs">
              {logs.filter((l) => l.kind !== "event").length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs text-center p-4">
                  <Terminal className="w-6 h-6 mb-2 text-slate-300" />
                  <span>Execute any of the 7 tests on the left to inspect responses.</span>
                </div>
              ) : (
                logs
                  .filter((l) => l.kind !== "event")
                  .map((log) => (
                    <div
                      key={log.id}
                      className={`p-3 rounded-xl border ${
                        log.kind === "success"
                          ? "bg-emerald-50/60 border-emerald-200 text-emerald-950"
                          : log.kind === "error"
                          ? "bg-rose-50/60 border-rose-200 text-rose-950"
                          : "bg-slate-50 border-slate-200 text-slate-800"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1 text-[11px]">
                        <span className="font-bold flex items-center gap-1.5">
                          {log.kind === "success" ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          ) : log.kind === "error" ? (
                            <XCircle className="w-3.5 h-3.5 text-rose-600" />
                          ) : (
                            <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
                          )}
                          <span>{log.title}</span>
                        </span>
                        <span className="font-mono text-slate-500">
                          {log.durationMs ? `${log.durationMs}ms · ` : ""}
                          {log.time}
                        </span>
                      </div>
                      <pre className="font-mono text-[11px] overflow-x-auto whitespace-pre-wrap bg-white/80 p-2 rounded-lg border border-slate-200/60 mt-1.5 max-h-48">
                        {pretty(log.detail)}
                      </pre>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reference Summary Table: The 7 Verified Operations */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#008069]" />
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Verified WhatsApp Integration Matrix (Tested & Delivered)
            </h3>
          </div>
          <span className="text-xs text-slate-500">
            Click any row to load into tester
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600 font-bold border-b border-slate-200">
              <tr>
                <th className="py-3 px-4 w-12 text-center">#</th>
                <th className="py-3 px-4">Message Type</th>
                <th className="py-3 px-4">API Endpoint</th>
                <th className="py-3 px-4">Payload / Method</th>
                <th className="py-3 px-4">WhatsApp Server ID</th>
                <th className="py-3 px-4">Status on Device</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {VERIFIED_OPERATIONS.map((op) => (
                <tr
                  key={op.num}
                  onClick={() => setActiveTab(op.id)}
                  className={`hover:bg-slate-50/80 cursor-pointer transition-colors ${
                    activeTab === op.id ? "bg-emerald-50/40" : ""
                  }`}
                >
                  <td className="py-3 px-4 text-center font-bold text-slate-500">{op.num}</td>
                  <td className="py-3 px-4 font-bold text-slate-900">{op.type}</td>
                  <td className="py-3 px-4 font-mono text-[11px] text-slate-700">{op.endpoint}</td>
                  <td className="py-3 px-4 font-mono text-[11px] text-slate-600 max-w-xs truncate">
                    {op.payload}
                  </td>
                  <td className="py-3 px-4 font-mono text-[11px] text-slate-800 font-semibold">
                    {op.serverId}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border ${op.statusColor}`}
                    >
                      {op.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
